import React from 'react';
import { defineMessages, type IntlShape } from 'react-intl';

import type {
	ExtensionManifest,
	ExtensionModule,
} from '@atlaskit/editor-common/extensions/extension-manifest';
import AiGenerativeTextIcon from '@atlaskit/icon-lab/core/ai-generative-text';

import { expVal } from '@confluence/feature-experiments';
import type { OpenModalParams } from '@confluence/smart-creation-modal-store/entry-points/smartCreationModalStore.tsx';

export const i18n: {
	title: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	description: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	title: {
		id: 'experiment-create-with-rovo-quick-insert.create-with-rovo-extension.title',
		defaultMessage: 'Create with Rovo',
		description: 'Title for Create with Rovo item in the elements menu',
	},
	description: {
		id: 'experiment-create-with-rovo-quick-insert.create-with-rovo-extension.description',
		defaultMessage: 'Use Rovo to create new content on the page',
		description: 'Description for Create with Rovo item in the elements menu',
	},
});

const getPriority = () => {
	switch (expVal('cc_fd_cwr_quick_insert', 'cohort', 'control')) {
		case 'slot-two':
			return -400;
		case 'slot-four':
			return -50;
		default:
			return undefined;
	}
};

export const createCreateWithRovoExtension = (
	intl: IntlShape,
	pageContext: { contentId: string; contentTitle?: string; spaceKey?: string },
	openSmartCreationModal: (params: OpenModalParams) => void,
): ExtensionManifest => {
	const { contentId, contentTitle, spaceKey } = pageContext;

	// current page will become new parent content
	const parentContent = {
		id: contentId,
		title: contentTitle ?? '',
		contentType: 'page',
	};

	const quickInsertModule: ExtensionModule = {
		key: 'create-with-rovo',
		title: intl.formatMessage(i18n.title),
		description: intl.formatMessage(i18n.description),
		keywords: ['create with rovo', 'rovo', 'ai', 'create'],
		categories: ['AI'],
		priority: getPriority(),
		featured: true,
		icon: async () => () => <AiGenerativeTextIcon label="" />,
		action: async () => {
			// this callback should be the callback from `useOpenSmartCreationModalFromQuickInsert`
			openSmartCreationModal({
				entryPoint: 'editorQuickInsert',
				spaceKey,
				parentContent,
				content: { id: contentId, type: 'page' },
			});
			// Don't insert any node; just open the modal
		},
	};

	return {
		title: intl.formatMessage(i18n.title),
		description: intl.formatMessage(i18n.description),
		type: 'com.atlassian.confluence.macro.core',
		key: 'create-with-rovo-extension',
		icons: {
			'48': async () => () => <AiGenerativeTextIcon label="" />,
		},
		modules: {
			quickInsert: [quickInsertModule],
		},
	};
};
